import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import MediaGrid from '@/components/base/MediaGrid';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import FilterBar from '@/pages/category/components/FilterBar';
import { filterMedia, TYPE_DESCRIPTIONS } from '@/lib/media';
import { TYPE_LABELS, type MediaType } from '@/types/media';

const PAGE_SIZE = 6;
const VALID_TYPES: MediaType[] = ['movie', 'tv', 'variety'];

export default function Category() {
  const { type = '' } = useParams();
  const mediaType = type as MediaType;

  const [year, setYear] = useState('全部');
  const [region, setRegion] = useState('全部');
  const [tag, setTag] = useState('全部');
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isValid = VALID_TYPES.includes(mediaType);

  const result = useMemo(() => {
    if (!isValid) return [];
    const filtered = filterMedia(mediaType, { year, region, tag });
    const sorted = [...filtered].sort((a, b) => {
      if (sort === 'hot') return b.rating - a.rating;
      return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    });
    return sorted;
  }, [mediaType, year, region, tag, sort, isValid]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = result.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const applyFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const handleReset = () => {
    setYear('全部');
    setRegion('全部');
    setTag('全部');
    setPage(1);
  };

  if (!isValid) {
    return (
      <div className="min-h-screen flex flex-col bg-background-100">
        <Header />
        <main className="flex-1 w-full px-4 md:px-8 py-16">
          <EmptyState
            icon="ri-error-warning-line"
            title="分类不存在"
            description="你访问的分类暂不存在，请从下方入口重新选择。"
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer"
              >
                返回首页
              </Link>
            }
          />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-100">
      <Header />

      <div className="w-full px-4 md:px-8 py-3 border-b border-background-200 bg-background-50">
        <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="whitespace-nowrap">影视分类</span>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-900 font-medium whitespace-nowrap">{TYPE_LABELS[mediaType]}</span>
        </nav>
      </div>

      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-950">
            {TYPE_LABELS[mediaType]}
            <span className="ml-2 text-base md:text-lg font-medium text-foreground-400">分类</span>
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-2xl">
            {TYPE_DESCRIPTIONS[mediaType]}
          </p>
        </div>

        <FilterBar
          year={year}
          region={region}
          tag={tag}
          sort={sort}
          mobileOpen={mobileOpen}
          onToggleMobile={() => setMobileOpen((prev) => !prev)}
          onYearChange={applyFilter(setYear)}
          onRegionChange={applyFilter(setRegion)}
          onTagChange={applyFilter(setTag)}
          onSortChange={(value) => {
            setSort(value);
            setPage(1);
          }}
          onReset={handleReset}
        />

        <div className="flex items-center justify-between mt-6 mb-4">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-semibold text-accent-600">{result.length}</span> 部内容
          </p>
          <p className="text-sm text-foreground-500">
            第 {safePage} / {totalPages} 页
          </p>
        </div>

        {pageItems.length > 0 ? (
          <>
            <MediaGrid items={pageItems} columns={4} />
            <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <EmptyState
            icon="ri-search-eye-line"
            title="没有找到匹配的内容"
            description="当前筛选条件下暂无影视资源，试试调整或清空筛选条件。"
            action={
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                清空筛选
              </button>
            }
          />
        )}
      </main>

      <Footer />
    </div>
  );
}